import React from 'react';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BreadcrumbHomeIcon } from '../../components/Icons/LivisIcons';
import { getSelectedUsecase, getWorkstationData } from '../../helpers/storage';
import { useNavigate } from 'react-router-dom';

interface LivisBreadcrumbProps {
  title?: string;
  className?: string;
  showHome?: boolean;
  homeLabel?: string;
  // If true, show 'Station: <workstation>' for the first non-home segment
  showStationLabel?: boolean;
  // If true, show the last segment
  showLastSegment?: boolean;
}

const LivisBreadcrumb: React.FC<LivisBreadcrumbProps> = ({
  title,
  className = "",
  showHome = true,
  homeLabel,
  showStationLabel = false,
  showLastSegment = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Internal navigation handlers (currently console logging)
  const handleHome = () => {
    // navigate('/');
    console.log('breadcrumb: home');
    navigate("/app/inspection/station")
  };

  const handleStation = (value?: string) => {
    // navigate('/stations');
    console.log('breadcrumb: station', value || '');
    const selectedStation = getWorkstationData()
    const stationAvailable = selectedStation?.workstation_name?.toLowerCase() === value?.toLowerCase()
    if(stationAvailable){
      navigate(`/app/usecase/list/${selectedStation?._id}`)
    }else{
      navigate("/app/inspection/station")
    }
  };

  const handleAIInspector = (value?: string) => {
    // navigate('/ai-inspector');
    console.log('breadcrumb: aiInspector', value || '');
    const selectedAiInsp = getSelectedUsecase()
    const inspectorAvailable = selectedAiInsp?.usecase_name?.toLowerCase() === value?.toLowerCase()
    if(inspectorAvailable){
      navigate(`/app/part/list/${selectedAiInsp?._id}`)
    }else{
      navigate("/app/inspection/station")
    }
  };

  const handleParts = (value?: string) => {
    // navigate('/parts');
    console.log('breadcrumb: parts', value || '');
  };

  // Arrow icon for separator
  const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Function to parse title and generate structured breadcrumb segments
  const generateBreadcrumbFromTitle = (titleString: string) => {
    // Clean the title - remove LIVIS suffix and user info
    const cleanTitle = titleString
      .replace(/\s*\|\s*LIVIS.*$/, '')
      .replace(/\s*-\s*[A-Z][a-z]+\s*\([^)]+\)\s*$/, '')
      .trim();

    const parts = cleanTitle.split(' - ').map(part => part.trim()).filter(Boolean);

    // Expectation from caller: parts[0] = first label value, parts[1] = usecase name, parts[2] (optional) = parts
    const firstValue = parts[0];
    const usecaseName = parts[1];
    const partsName = parts[2];

    type Segment = {
      key: 'home' | 'station' | 'aiInspector' | 'usecase' | 'parts' | 'generic';
      label?: string; // e.g., "Station", "AI Inspector"
      value?: string; // e.g., actual name
      text?: string;  // fallback plain text for generic segments
      onClick?: () => void;
    };

    const segments: Segment[] = [];

    if (showHome) {
      const homeText = homeLabel || String(t('Home'));
      segments.push({ key: 'home', text: homeText, onClick: () => handleHome() });
    }

    // First non-home segment: show only value (no label like "Station:")
    if (firstValue) {
      if (showStationLabel) {
        segments.push({ key: 'station', label: String(t('Station')), value: firstValue, onClick: () => handleStation(firstValue) });
      } else {
        segments.push({ key: 'station', value: firstValue, onClick: () => handleStation(firstValue) });
      }
    }

    if (usecaseName) {
      // Render as AI Inspector: <usecase>
      segments.push({ key: 'aiInspector', label: String(t('AI Inspector')), value: usecaseName, onClick: () => handleAIInspector(usecaseName) });
    }

    // If there are more parts, append them generically
    if (partsName) {
      segments.push({ key: 'parts', text: partsName, onClick: () => handleParts(partsName) });
    } else if (parts.length > 2) {
      for (let i = 2; i < parts.length; i++) {
        segments.push({ key: 'generic', text: parts[i], onClick: () => handleParts(parts[i]) });
      }
    }

    return segments;
  };

  // Determine breadcrumb labels
  let breadcrumbSegments: ReturnType<typeof generateBreadcrumbFromTitle> = [];
  
  if (title) {
    breadcrumbSegments = generateBreadcrumbFromTitle(title);
  } else {
    breadcrumbSegments = generateBreadcrumbFromTitle(document.title);
  }

  if (breadcrumbSegments.length === 0) {
    return null;
  }

  return (
    <Box className={`livis-breadcrumb-container ${className}`}>
      <Breadcrumbs
        separator={<ArrowIcon />}
        className="livis-breadcrumb"
      >
        {breadcrumbSegments.map((seg, index) => {
          const isHome = seg.key === 'home';
          const isLast = index === breadcrumbSegments.length - 1;
          const clickable = !!seg.onClick && !isLast; // last item should not be clickable
          
          return (
            <Box
              key={index}
              className={`${isLast ? 'livis-breadcrumb-active' : 'livis-breadcrumb-link'} livis-breadcrumb-segment ${clickable ? 'is-clickable' : ''} ${isHome ? 'livis-breadcrumb-home' : ''}`}
              display="flex"
              alignItems="center"
              gap={0.5}
              onClick={clickable ? seg.onClick : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
            >
              {isHome && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <BreadcrumbHomeIcon />
                  <span className='livis-breadcrumb-link-text' style={{paddingTop: '0.2rem'}}>{t('Home')}</span>
                </div>
              )}

              {!isHome && (
                <Typography className={isLast ? 'livis-breadcrumb-active-text' : 'livis-breadcrumb-link-text'}>
                  {isLast
                    ? (
                      // Last item: by default show only value; if showLastSegment and label exists, show label + value
                      showLastSegment && seg.label
                        ? (
                          <>
                            <span className="livis-breadcrumb-label">{seg.label}</span>
                            <span className="livis-breadcrumb-separator">: </span>
                            <span className="livis-breadcrumb-value">{seg.value || ''}</span>
                          </>
                        )
                        : (
                          <>
                            {seg.value ? (
                              <span className="livis-breadcrumb-value">{seg.value}</span>
                            ) : (
                              <span className="livis-breadcrumb-value">{String(t(seg.text || ''))}</span>
                            )}
                          </>
                        )
                    )
                    : seg.label
                      ? (
                        <>
                          <span className="livis-breadcrumb-label">{seg.label}</span>
                          <span className="livis-breadcrumb-separator">: </span>
                          <span className="livis-breadcrumb-value">{seg.value || ''}</span>
                        </>
                      )
                      : (
                        <>
                          {seg.value ? (
                            <span className="livis-breadcrumb-value">{seg.value}</span>
                          ) : (
                            String(t(seg.text || ''))
                          )}
                        </>
                      )
                  }
                </Typography>
              )}
            </Box>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default LivisBreadcrumb;